import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaChevronRight, FaSearch } from 'react-icons/fa';
import { getLoanSubscribersList } from '../services/loanSubscribersService';
import { useAuth } from '../auth/AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getCurrentRole = () => {
  const authRole = localStorage.getItem('authRole');
  const roleMapping = {
    wallet_superadmin: 'SuperAdmin',
    wallet_finance: 'Finance',
    wallet_ops: 'Ops',
    wallet_support: 'Support',
  };
  return roleMapping[authRole] || null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return '—'; }
};

const humanize = (str) => {
  if (!str) return '—';
  return str.replace(/-/g, ' ').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// ── Page ──────────────────────────────────────────────────────────────────────

const LoanSubscribersList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const currentRole = getCurrentRole();

  const PAGE_SIZE = 20;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  const [nameInput, setNameInput] = useState(() => searchParams.get('search') || '');
  const [nameFilter, setNameFilter] = useState(() => searchParams.get('search') || '');
  const [page, setPage] = useState(() => Number(searchParams.get('page') || 1));

  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setNameFilter(nameInput.trim());
      setPage(1);
    }, 700);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [nameInput]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (nameFilter.trim()) params.set('search', nameFilter.trim());
    setSearchParams(params, { replace: true });
  }, [page, nameFilter, setSearchParams]);

  const fetchData = useCallback(async () => {
    if (authLoading || !isAuthenticated || !currentRole) return;
    setLoading(true);
    setError('');
    try {
      const res = await getLoanSubscribersList(currentRole, {
        page, items: PAGE_SIZE, search: nameFilter,
      });
      if (!res?.metadata?.api_status) throw new Error('API returned an error status');
      const pg = res.msg.pagination || {};
      const totalPages = Math.ceil((pg.total || 0) / (pg.items || PAGE_SIZE));
      setItems(res.msg.items || []);
      setPagination({ ...pg, total_pages: totalPages });
    } catch (err) {
      setError(err.message || 'Failed to load loan subscribers.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, currentRole, page, nameFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access loan subscribers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">

        {/* Back to Home */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        {/* Main card — header is sticky, table scrolls */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 flex flex-col" style={{ maxHeight: 'calc(100vh - 180px)' }}>

          {/* ── Fixed header: title + filters ── */}
          <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Loan Subscribers</h2>
              {!loading && pagination.total > 0 && (
                <span className="text-sm text-gray-500">{pagination.total} subscriber{pagination.total !== 1 ? 's' : ''}</span>
              )}
            </div>

            {error && (
              <div className="mb-3 px-4 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center justify-between">
                <span>{error}</span>
                <button onClick={fetchData} className="text-xs underline ml-4 hover:text-red-900">Retry</button>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              {/* Search */}
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, mobile, ref code"
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-sm"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Scrollable table body ── */}
          <div className="flex-1 overflow-y-auto overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="w-[22%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscriber Name</th>
                  <th className="w-[15%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                  <th className="w-[14%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref Code</th>
                  <th className="w-[20%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                  <th className="w-[18%] px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="w-[11%] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="w-[4%] px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${50 + (j * 13) % 40}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No loan subscribers found
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/subscriber-wallet/loan-subscribers/${item.id}/detail`)}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate">
                        {item.subscriber_name || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate">
                        {item.mobile || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate">
                        {item.ref_code || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate" title={item.subscription_code || ''}>
                        {item.subscription_name || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-center">
                        {item.status ? humanize(item.status) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-400">
                        <FaChevronRight />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination (fixed at bottom) ── */}
          {pagination.total > 0 && (
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.items + 1}–{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Prev
                </button>
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoanSubscribersList;
